import type {SiteLocaleConfig, SiteMap} from '~/interfaces/multisite';
import {toValue} from 'vue';

const siteMapToArray = (sitesMap: SiteMap): SiteMap => Object.entries(sitesMap).map(([key, value]) => ({...value, uri: key}));

export const useLanguages = () => {
    const {activeSite, sites} = useMultisite();

    const url = useRequestURL();

    const getSitesLanguages = (group: string, type: string): SiteLocaleConfig[] => {
        if (!sites || !group) {
            return [];
        }

        const currentSite = toValue(activeSite);
        const supportedLocales = currentSite?.supportedLocales;

        let groupedSites = siteMapToArray(sites).filter(site =>
            site.group === group &&
            site.type === type
        );

        // Filter by supportedLocales if defined (this way we can launch a subset of languages to production)

        if (supportedLocales && supportedLocales.length > 0) {
            // Always include current locale
            const localesWithCurrent = [...new Set([...supportedLocales, currentSite.locale])];
            const filtered = groupedSites.filter(site =>
                localesWithCurrent.includes(site.locale)
            );
            // Only apply filter if it produces results (fallback to all on misconfiguration)
            if (filtered.length > 0) {
                groupedSites = filtered;
            }
        }

        const uniqueSitesByLanguage = new Map<string, SiteLocaleConfig>();

        groupedSites.forEach((site: any)  => {
            if (uniqueSitesByLanguage.has(site.language)) {
                return;
            }
            uniqueSitesByLanguage.set(site.language, site);
        });

        return Array.from(uniqueSitesByLanguage.values());
    };

    return computed(() => {
        if(!activeSite.value || !activeSite.value.group) {
            return [];
        }

        return getSitesLanguages(
            toValue(activeSite).group,
            toValue(activeSite).type
        ).map(site => ({
            title: site.language,
            rtl: site.rtl ?? undefined,
            uri: site?.uri,
            handle: site?.handle,
            locale: site?.locale,
            isProduction: !['local', 'dev'].includes(toValue(activeSite).type),
            active: site?.uri === url?.hostname,
        }));
    });
};
